import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Lock, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Logo } from "../components/Logo";
import { JobsTab } from "../components/careers/admin/JobsTab";
import { ApplicationsTab } from "../components/careers/admin/ApplicationsTab";
import { IntegrationsTab } from "../components/careers/admin/IntegrationsTab";
import { API, http, authHeaders, formatApiError, CAREERS_TOKEN } from "../lib/api";

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await http.post(`${API}/auth/login`, { email, password, portal: "careers" });
      localStorage.setItem(CAREERS_TOKEN, data?.token ?? "");
      onLogin(data?.user ?? null);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="grid min-h-screen place-items-center px-5" data-testid="careers-admin-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-md border border-white/10 bg-[#111519] p-8">
        <Link to="/" data-testid="careers-admin-home-link"><Logo /></Link>
        <p className="font-display mt-8 text-3xl">Careers admin</p>
        <p className="mt-1 text-sm text-zinc-400">Manage job listings and candidate CVs.</p>
        <input type="email" required className="field mt-8" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="careers-admin-email" />
        <input type="password" required className="field mt-4" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="careers-admin-password" />
        {error && <p className="mt-4 text-sm text-red-400" data-testid="careers-admin-login-error">{error}</p>}
        <button disabled={loading} className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 py-3 text-sm font-medium text-[#0a0c0e] hover:bg-emerald-400 disabled:opacity-60" data-testid="careers-admin-login-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Sign in
        </button>
      </form>
    </div>
  );
};

export default function CareersAdmin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [params, setParams] = useSearchParams();
  const driveFlag = params.get("drive");

  useEffect(() => {
    if (!localStorage.getItem(CAREERS_TOKEN)) {
      setChecking(false);
      return;
    }
    http.get(`${API}/auth/careers/me`, { headers: authHeaders(CAREERS_TOKEN) })
      .then(({ data }) => setUser(data ?? null))
      .catch(() => localStorage.removeItem(CAREERS_TOKEN))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!driveFlag) return;
    if (driveFlag === "connected") toast.success("Google Drive connected");
    else toast.error("Google Drive connection didn't complete. Please try again.");
    setParams({}, { replace: true });
  }, [driveFlag, setParams]);

  const logout = useCallback(() => {
    localStorage.removeItem(CAREERS_TOKEN);
    setUser(null);
  }, []);

  if (checking) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="min-h-screen" data-testid="careers-admin-dashboard">
      <header className="border-b border-white/10">
        <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-5 sm:px-8">
          <Link to="/careers" data-testid="careers-admin-site-link"><Logo /></Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-zinc-400">{user?.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:border-emerald-400" data-testid="careers-admin-logout"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] px-5 py-12 sm:px-8">
        <p className="eyebrow">Careers admin</p>
        <h1 className="font-display mt-3 text-5xl">Job board & talent record</h1>
        <Tabs defaultValue={driveFlag ? "integrations" : "jobs"} className="mt-10">
          <TabsList className="bg-[#111519] border border-white/10" data-testid="careers-admin-tabs">
            <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">Applications & CVs</TabsTrigger>
            <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
          </TabsList>
          <TabsContent value="jobs" className="mt-8"><JobsTab onAuthError={logout} /></TabsContent>
          <TabsContent value="applications" className="mt-8"><ApplicationsTab onAuthError={logout} /></TabsContent>
          <TabsContent value="integrations" className="mt-8"><IntegrationsTab onAuthError={logout} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
